/**
 * Monta a hierarquia de contas contábeis a partir de uma lista plana.
 *
 * O vínculo pai/filho usa o campo `conta_superior` (CT1_CTASUP). Contas cujo
 * pai não está presente na lista (ex.: pai bloqueado/filtrado) viram raízes.
 *
 * @param {Array<object>} rows - linhas com { conta, conta_superior, ... }
 * @returns {{ childrenMap: Map<string, object[]>, roots: object[] }}
 */
export function buildAccountTree(rows) {
    const list = Array.isArray(rows) ? rows : [];
    const byConta = new Map();
    list.forEach((row) => byConta.set(row.conta, row));

    const childrenMap = new Map();
    const roots = [];

    list.forEach((row) => {
        const parentConta = row.conta_superior;
        const hasParent = parentConta && parentConta !== row.conta && byConta.has(parentConta);

        if (hasParent) {
            const children = childrenMap.get(parentConta) || [];
            children.push(row);
            childrenMap.set(parentConta, children);
        } else {
            roots.push(row);
        }
    });

    return { childrenMap, roots };
}

/**
 * Achata a árvore em uma lista respeitando o estado de expansão, anexando
 * `__depth` (nível de indentação) e `__hasChildren` a cada linha.
 *
 * @param {Array<object>} rows
 * @param {Set<string>} expandedSet - contas atualmente expandidas
 * @param {{ expandAll?: boolean }} [options]
 * @returns {Array<object>}
 */
export function flattenAccountTree(rows, expandedSet, { expandAll = false } = {}) {
    const { childrenMap, roots } = buildAccountTree(rows);
    const out = [];

    const walk = (node, depth) => {
        const children = childrenMap.get(node.conta) || [];
        const hasChildren = children.length > 0;

        out.push({ ...node, __depth: depth, __hasChildren: hasChildren });

        if (hasChildren && (expandAll || expandedSet.has(node.conta))) {
            children.forEach((child) => walk(child, depth + 1));
        }
    };

    roots.forEach((root) => walk(root, 0));
    return out;
}

/**
 * Retorna os códigos de todas as contas que possuem filhas (contas pai).
 *
 * @param {Array<object>} rows
 * @returns {string[]}
 */
export function getParentContas(rows) {
    const { childrenMap } = buildAccountTree(rows);
    return Array.from(childrenMap.keys());
}
